// Session-scoped registry of outbound message ids the AI leasing agent
// auto-sent (auto_response mode).
//
// Why this exists: the authoritative `suggestion_auto_sent` websocket event
// names the outbound message, but that message's own fetch (triggered by the
// `new_message` event) can race the backend linking the suggestion row and read
// `ai_auto_sent=false`. Any code path that writes the message into the query
// cache — the single-message upsert, a list refetch (`suggestion_*` handlers),
// pagination, a manual refetch — could otherwise downgrade the "Auto-sent by
// the AI leasing agent" header back to a plain "Sent by System".
//
// Making this a module-level registry (not a hook-local ref) lets BOTH the
// socket handler and the messages query's `select` re-stamp the flag, so the
// label is stable regardless of how or when the cache is (re)populated.

// Bounded FIFO so a long-lived inbox session can't grow this without limit.
// Sets preserve insertion order, so the first key is the oldest.
const MAX_TRACKED = 500;
const autoSentMessageIds = new Set<string>();

export const markAutoSent = (id: string): void => {
  autoSentMessageIds.add(id);
  if (autoSentMessageIds.size > MAX_TRACKED) {
    const oldest = autoSentMessageIds.values().next().value;
    if (oldest !== undefined) autoSentMessageIds.delete(oldest);
  }
};

const isAutoSent = (id: string): boolean => autoSentMessageIds.has(id);

/** True when at least one id is tracked — lets read paths skip work entirely. */
export const hasAutoSent = (): boolean => autoSentMessageIds.size > 0;

/**
 * Re-stamp the AI-auto-sent flags on a message when its id is tracked. Returns
 * the same object reference when untracked so callers/structural-sharing don't
 * churn. `from_ai_suggestion` is forced too so the AI avatar/header render.
 */
export const applyAutoSentFlags = <
  T extends { id: string; ai_auto_sent?: boolean; from_ai_suggestion?: boolean }
>(
  message: T
): T =>
  isAutoSent(message.id)
    ? { ...message, ai_auto_sent: true, from_ai_suggestion: true }
    : message;
